// Staff-auth client (copied from optireach-web; keys renamed or_* → rw_*).
// The token pair lives in localStorage ('rw_access'/'rw_refresh'); a silent refresh is
// scheduled at expiresIn-180s; a failed refresh clears the pair (→ login screen).
// Plain helpers are exported for api.ts (authFetch); the hook is the React binding App.tsx gates on.
import { useCallback, useEffect, useState } from 'react';

const ACCESS_KEY = 'rw_access';
const REFRESH_KEY = 'rw_refresh';

const AUTH_URL: string =
  (import.meta.env.VITE_STAFF_AUTH_URL as string | undefined) || 'http://localhost:4001';
/** Dev bypass — skips the login gate entirely (mirror of the api's STAFF_AUTH_DISABLED). */
export const AUTH_DISABLED: boolean =
  (import.meta.env.VITE_STAFF_AUTH_DISABLED as string | undefined) === 'true';

export interface StaffUser {
  email: string;
  roles: string[];
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  user?: StaffUser;
}

// ── token store (module-level so api.ts can use it outside React) ────────────

const listeners = new Set<() => void>();
function notify(): void {
  for (const l of listeners) l();
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  notify();
}

function storeTokens(pair: TokenPair): void {
  localStorage.setItem(ACCESS_KEY, pair.accessToken);
  localStorage.setItem(REFRESH_KEY, pair.refreshToken);
  notify();
}

/** Decode a JWT payload WITHOUT verifying — display + refresh scheduling only; the api verifies. */
export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1] ?? '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function tokenUser(token: string | null): StaffUser | null {
  if (!token) return null;
  const p = decodeJwt(token);
  const email = typeof p?.email === 'string' ? p.email : typeof p?.sub === 'string' ? p.sub : '';
  if (!email) return null;
  const roles = Array.isArray(p?.roles)
    ? (p.roles as unknown[]).filter((r): r is string => typeof r === 'string')
    : [];
  return { email, roles };
}

function tokenExpMs(token: string): number | null {
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

// ── issuer calls ─────────────────────────────────────────────────────────────

async function postAuth(path: string, body: Record<string, unknown>): Promise<Response> {
  return fetch(`${AUTH_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

let refreshInflight: Promise<string | null> | null = null;

/** One refresh at a time; resolves the new access token, or null after clearing the pair. */
export function refreshAccessToken(): Promise<string | null> {
  refreshInflight ??= doRefresh().finally(() => {
    refreshInflight = null;
  });
  return refreshInflight;
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearTokens();
    return null;
  }
  try {
    const res = await postAuth('/auth/refresh', { refreshToken });
    if (!res.ok) {
      clearTokens();
      return null;
    }
    const pair = (await res.json()) as TokenPair;
    storeTokens(pair);
    return pair.accessToken;
  } catch {
    // issuer unreachable: keep the pair — live access tokens stay valid ≤15min
    return null;
  }
}

export async function loginWithGoogle(googleIdToken: string): Promise<void> {
  const res = await postAuth('/auth/token', { googleIdToken });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`sign-in rejected: ${res.status} ${body.slice(0, 200)}`);
  }
  storeTokens((await res.json()) as TokenPair);
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await postAuth('/auth/logout', { refreshToken });
    } catch {
      /* best-effort — clearing locally is what matters */
    }
  }
  clearTokens();
}

// ── React binding ────────────────────────────────────────────────────────────

export interface StaffAuth {
  user: StaffUser | null;
  disabled: boolean;
  login: (googleIdToken: string) => Promise<void>;
  logout: () => Promise<void>;
}

const REFRESH_LEAD_MS = 180_000; // refresh at expiresIn-180s per contract

export function useStaffAuth(): StaffAuth {
  const [, setBump] = useState(0);

  useEffect(() => {
    const l = (): void => setBump((b) => b + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  const access = AUTH_DISABLED ? null : getAccessToken();

  // Silent refresh timer, rescheduled whenever the access token changes.
  useEffect(() => {
    if (AUTH_DISABLED || !access) return;
    const expMs = tokenExpMs(access);
    const delay = expMs === null ? 0 : Math.max(expMs - Date.now() - REFRESH_LEAD_MS, 0);
    const id = window.setTimeout(() => void refreshAccessToken(), delay);
    return () => window.clearTimeout(id);
  }, [access]);

  const login = useCallback((googleIdToken: string) => loginWithGoogle(googleIdToken), []);
  const doLogout = useCallback(() => logout(), []);

  const user: StaffUser | null = AUTH_DISABLED
    ? { email: 'dev@optisigns.com', roles: ['root'] }
    : tokenUser(access);

  return { user, disabled: AUTH_DISABLED, login, logout: doLogout };
}
